import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function AdminPostEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [title, setTitle] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [content, setContent] = useState("");
  const [tags, setTags] = useState("");
  const [coverImage, setCoverImage] = useState("");
  const [published, setPublished] = useState(true);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isEdit) return;
    api
      .get("/blog/admin/all")
      .then((r) => {
        const found = r.data.find((p) => p.id === id);
        if (!found) return navigate("/admin");
        setTitle(found.title);
        setExcerpt(found.excerpt || "");
        setContent(found.content);
        setTags((found.tags || []).join(", "));
        setCoverImage(found.cover_image || "");
        setPublished(found.published);
      })
      .finally(() => setLoading(false));
  }, [id, isEdit, navigate]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    const payload = {
      title,
      excerpt,
      content,
      tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      cover_image: coverImage || null,
      published,
    };
    try {
      if (isEdit) await api.put(`/blog/${id}`, payload);
      else await api.post(`/blog`, payload);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-24 font-mono text-xs uppercase tracking-widest text-[#94A3B8]">Loading...</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20" data-testid="admin-editor">
      <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-[#94A3B8] hover:text-white mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to dashboard
      </Link>
      <div className="overline mb-2">/admin — {isEdit ? "edit" : "new"} post</div>
      <h1 className="font-display font-black text-white text-3xl lg:text-5xl tracking-tighter mb-10">
        {isEdit ? "Edit post" : "New post"}
      </h1>

      <form onSubmit={save} className="space-y-6">
        <Field label="Title" required>
          <input
            type="text"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="editor-title"
            className="w-full bg-[#0A0C10] border border-[#272E3A] focus:border-[#FF5500] focus:outline-none px-4 py-3 text-white transition-colors"
          />
        </Field>

        <Field label="Excerpt">
          <textarea
            rows={2}
            value={excerpt}
            onChange={(e) => setExcerpt(e.target.value)}
            data-testid="editor-excerpt"
            className="w-full bg-[#0A0C10] border border-[#272E3A] focus:border-[#FF5500] focus:outline-none px-4 py-3 text-white transition-colors"
            placeholder="Short summary shown in the blog list"
          />
        </Field>

        <Field label="Cover image URL">
          <input
            type="url"
            value={coverImage}
            onChange={(e) => setCoverImage(e.target.value)}
            data-testid="editor-cover"
            className="w-full bg-[#0A0C10] border border-[#272E3A] focus:border-[#FF5500] focus:outline-none px-4 py-3 text-white font-mono text-sm transition-colors"
            placeholder="https://..."
          />
        </Field>

        <Field label="Tags (comma-separated)">
          <input
            type="text"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            data-testid="editor-tags"
            className="w-full bg-[#0A0C10] border border-[#272E3A] focus:border-[#FF5500] focus:outline-none px-4 py-3 text-white transition-colors"
            placeholder="meshcore, setup, repeater"
          />
        </Field>

        <Field label="Content (Markdown)" required>
          <textarea
            required
            rows={18}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            data-testid="editor-content"
            className="w-full bg-[#0A0C10] border border-[#272E3A] focus:border-[#FF5500] focus:outline-none px-4 py-3 text-white font-mono text-sm transition-colors"
            placeholder="## Heading&#10;&#10;Your content here. Supports ## headings, - lists, **bold**, and `code`."
          />
        </Field>

        <label className="flex items-center gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={published}
            onChange={(e) => setPublished(e.target.checked)}
            data-testid="editor-published"
            className="w-4 h-4 accent-[#FF5500]"
          />
          <span className="text-slate-300 font-mono text-xs uppercase tracking-widest">Published (visible on /blog)</span>
        </label>

        {error && (
          <div className="text-sm text-[#EF4444] border border-[#EF4444]/30 bg-[#EF4444]/10 px-4 py-3 font-mono" data-testid="editor-error">
            {error}
          </div>
        )}

        <div className="flex gap-3 pt-4 border-t border-[#272E3A]">
          <button
            type="submit"
            disabled={saving}
            data-testid="editor-save-btn"
            className="px-6 py-3 bg-[#FF5500] hover:bg-[#FF7326] disabled:opacity-50 text-white font-semibold flex items-center gap-2 transition-colors"
          >
            <Save className="w-4 h-4" /> {saving ? "Saving..." : isEdit ? "Save changes" : "Publish post"}
          </button>
          <Link
            to="/admin"
            className="px-6 py-3 border border-[#272E3A] hover:border-[#FF5500] text-slate-300 hover:text-white transition-colors"
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}

const Field = ({ label, required, children }) => (
  <div>
    <label className="block font-mono text-xs uppercase tracking-widest text-[#94A3B8] mb-2">
      {label} {required && <span className="text-[#FF5500]">*</span>}
    </label>
    {children}
  </div>
);
